/**
 * The single classification a `(col …)` reference needs: is its head a
 * reference to the current row, to a foreign bound dataset, or to
 * nothing (a typo)?
 *
 * This is the SSOT for "row vs foreign vs unbound", shared by the
 * cross-dataset set-ref pass (`inlineSetRefs`) and the static
 * type-check (`validateRefs`). It asks ONE scope question per
 * candidate — it owns no schema, no `externalId`/`space` allow-list,
 * and no column-existence check. Whether a column named by a row ref
 * actually exists is validated downstream (against the view schema /
 * the live row); whether a foreign column exists is validated when it
 * is resolved.
 *
 * A `(col …)` with an expression head (Form 2, e.g. `(col (:rel %) :x)`,
 * for which `colNodeLabels` returns `undefined`) is only meaningful on
 * the current row, so it classifies as `row`.
 */
export type ColRefKind = 'row' | 'foreign' | 'unbound';

export type ColRefScope = {
  /** Names bound to the current row in this lexical scope — the
   *  lambda's parameter(s) (`%`/`%1`/`%2` or a named `(fn [x] …)` param). */
  readonly rowAliases: ReadonlySet<string>;
  /** Does `name` resolve to a dataset bound in scope (a `(def …)` /
   *  session binding, or env-bound `Dataset`)? */
  readonly isBoundDataset: (name: string) => boolean;
};

/**
 * Classify a `(col …)`'s head from its label list. `labels` is the
 * output of `colNodeLabels` (or a statically-extracted `colLabels`):
 * `undefined`/empty means an expression head, which is row-scoped.
 *
 *   - `row`     — head is a lambda row parameter (`%`/`%1`/…), or an
 *                 expression head. The labels index into the row.
 *   - `foreign` — head names a dataset bound in scope: a cross-dataset
 *                 set-ref to inline.
 *   - `unbound` — head is neither. The per-row compiler resolves it
 *                 against the row (a bare-column head, sugar for
 *                 `(col % head …)`) or env/session (a scalar binding),
 *                 and throws `UnboundSymbolError` if it resolves to
 *                 nothing. Not decidable from scope facts alone, so it
 *                 is deferred — never eagerly inlined.
 */
export const colRefKind = (labels: readonly string[] | undefined, scope: ColRefScope): ColRefKind => {
  if (labels === undefined || labels.length === 0) {
    return 'row';
  }
  const head = labels[0] as string;
  if (scope.rowAliases.has(head)) {
    return 'row';
  }
  if (scope.isBoundDataset(head)) {
    return 'foreign';
  }
  return 'unbound';
};
